import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { isApiError } from '../client/errors';
import { newIdempotencyKey } from '../client/idempotency';
import { ContentType } from '../content/contracts';
import { ApplyPromo, CheckoutQuote, CheckoutStatus, SignupAccount, } from './contracts';
/**
 * Buying one course, or one ticket — the single-item funnel (12 Oct 2026).
 *
 * ── Ownership is not entitlement, and this is the whole feature ───────────
 * A purchase unlocks **that item** and nothing else. The buyer keeps
 * `entitled: false`, keeps every other paywall and keeps the subscription
 * offer in front of them; the one row they own reads `locked: false`. Nothing
 * in here writes a subscription, and nothing anywhere should infer one.
 *
 * So there is no "is this person a purchaser" flag to hang a screen on. The
 * two questions a screen can ask are: *is this row locked* (the row answers
 * it) and *what do I own* (`GET /me/purchases` answers it).
 *
 * ── Two contracts over one table ──────────────────────────────────────────
 * `PurchaseCheckout` sits beside `CheckoutSession` rather than widening it:
 * `planKey` and `planName` are required strings that three clients read, and
 * making them nullable to describe something that is not a membership would
 * break the membership funnel to no purpose. Same table, same quote, same
 * statuses, same `pay`, same `enter` — a different question answered.
 *
 * ── The id is the capability ──────────────────────────────────────────────
 * `POST /purchases` is public: a stranger buys, the account and the session
 * are created in one transaction, and holding the v4 uuid is what lets that
 * browser advance its own purchase. It appears in no listing and is kept out
 * of logs, exactly as the signup funnel's id is.
 */
/** What a purchase in progress looks like. `CheckoutStatus` is shared on purpose. */
export const PurchaseCheckout = z.object({
    id: z.string().uuid(),
    status: CheckoutStatus,
    /** `COURSE` or `EVENT` — nothing else is sellable, and the two read differently. */
    contentType: ContentType,
    contentItemId: z.string().uuid(),
    title: z.string(),
    /**
     * The stored quote. **Never recomputed here** — « Commande 56,34 $ » on the
     * success screen is this total and not a sum we assembled, because the
     * customer agreed to a number and a reprice must not move it under them.
     */
    quote: CheckoutQuote,
    promoCode: z.string().nullable(),
    /** Set once Stripe has been asked for a hosted page. */
    paymentUrl: z.string().nullable(),
    declineReason: z.string().nullable(),
    /**
     * **Payment is not access.** The webhook writes the purchase, so a paid
     * session sits in `PENDING_ACTIVATION` until it lands. This is the field the
     * success screen waits on; it is not `status === 'ACTIVE'` spelled twice.
     */
    grantsAccess: z.boolean(),
    locale: z.string().nullable(),
    createdAt: z.string().datetime(),
});
/** A stranger: the account is created with the purchase, in one transaction. */
export const StartPurchase = z.object({
    contentItemId: z.string().uuid(),
    account: SignupAccount,
    promoCode: z.string().max(40).optional(),
});
/** A member who is signed in — including a lapsed one, who is not entitled. */
export const StartMemberPurchase = z.object({
    contentItemId: z.string().uuid(),
    promoCode: z.string().max(40).optional(),
});
/**
 * One thing somebody owns outright.
 *
 * No rendered sentences: « Accès à vie · Pas commencée » and « Dans 13 jours »
 * are assembled by the screen from `accessEndsAt`, `progress` and
 * `event.startsAt`. `accessEndsAt` is the only honest source for a date — see
 * the field.
 */
export const MemberPurchase = z.object({
    contentItemId: z.string().uuid(),
    contentType: ContentType,
    title: z.string(),
    coverUrl: z.string().nullable(),
    priceCents: z.number().int(),
    currency: z.string().length(3),
    purchasedAt: z.string().datetime(),
    /**
     * **Null is « accès à vie »** — a course sold without a window, and a ticket
     * whose replay has none. Set wherever the offer carried one: a ticket whose
     * replay closes, and, since 12 October, a course with `accessDays`.
     *
     * Stamped at activation and **frozen**: it is the contract with *that*
     * buyer, so shortening the offer afterwards cannot reach backwards. Which
     * is why every line with a date in it reads this and never
     * `Course.accessDays`.
     */
    accessEndsAt: z.string().datetime().nullable(),
    /** A course's resume state, so the strip draws it without a second call. */
    progress: z
        .object({ modulesComplete: z.number().int(), modulesTotal: z.number().int() })
        .nullable(),
    /** A ticket's date. Null on a course. */
    event: z
        .object({ startsAt: z.string().datetime(), endsAt: z.string().datetime().nullable() })
        .nullable(),
});
/**
 * `owned` and `totalCourses` travel together because « Vous avez 1 formation
 * sur 20+ » is one sentence about two numbers, and a client holding one of
 * them eventually renders the other wrongly. `totalCourses` counts what **this
 * reader** may be shown, so it is a number their own library can reproduce.
 */
export const MemberPurchases = z.object({
    items: z.array(MemberPurchase),
    owned: z.number().int().nonnegative(),
    totalCourses: z.number().int().nonnegative(),
});
export const purchaseKeys = {
    one: (id) => ['purchase', id],
    mine: ['purchases', 'mine'],
};
/**
 * Poll a purchase in progress.
 *
 * Same shape as the signup funnel's watch and for the same reason: activation
 * is driven by Stripe's webhook, so the success screen watches rather than
 * assuming. It stops at a terminal state, and it keeps polling in a background
 * tab — the single most likely thing somebody does on « on prépare votre
 * accès » is switch to their mail to look for the receipt.
 */
export function usePurchase(http, id) {
    return useQuery({
        queryKey: purchaseKeys.one(id ?? ''),
        enabled: Boolean(id),
        queryFn: () => http.get(`/purchases/${id}`, { schema: PurchaseCheckout }),
        refetchInterval: (query) => {
            const status = query.state.data?.status;
            if (!status)
                return false;
            return status === 'PENDING_ACTIVATION' || status === 'AWAITING_PAYMENT' ? 3000 : false;
        },
        refetchIntervalInBackground: true,
    });
}
/** What this member owns outright. Empty for everybody who has bought nothing. */
export function useMyPurchases(http, enabled = true) {
    return useQuery({
        queryKey: purchaseKeys.mine,
        enabled,
        queryFn: () => http.get('/me/purchases', { schema: MemberPurchases }),
    });
}
/** A stranger buys. Public — no token is sent. */
export function useStartPurchase(http) {
    return useMutation({
        mutationFn: (input) => http.post('/purchases', input, {
            schema: PurchaseCheckout,
            idempotencyKey: newIdempotencyKey(),
        }),
    });
}
/**
 * A signed-in member buys, and goes straight to Stripe.
 *
 * One mutation for both calls, like `useBuyPlan`, because the screen has
 * nothing to show between them: they pressed « Acheter » and the next thing
 * they should see is the payment page. **Must be given the authenticated
 * client** — `mine` is the one route here that is not public.
 *
 * Both halves can refuse, and from today they refuse with the same codes:
 * §12.2 made `pay` answer `ALREADY_OWNED`, `ALREADY_ENTITLED`,
 * `CONTENT_NOT_FOR_SALE` and the rest, because a draft can sit for days and
 * the item can change under it. One mapper covers both for that reason.
 */
export function useBuyNow(http) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ contentItemId, promoCode, urls, }) => {
            const draft = await http.post('/purchases/mine', StartMemberPurchase.parse({ contentItemId, ...(promoCode ? { promoCode } : {}) }), { schema: PurchaseCheckout, idempotencyKey: newIdempotencyKey() });
            /*
             * A code was typed and did not stick on creation.
             *
             * `POST /purchases/mine` returns an **open draft for the same item
             * before it looks at `promoCode`**, so a member who opened this screen
             * once, came back with a code and typed it was charged the full price
             * with nothing saying so. That was ISS-03, and the guard below was the
             * only thing standing between them and a silent overcharge.
             *
             * **`POST /purchases/{id}/promo` now exists** (13 Oct 2026, measured
             * against the API rather than taken from the handoff, which still says
             * it is coming). So the guard stops being a dead end: attach the code to
             * the draft and take the re-quoted purchase back. DRAFT only, which is
             * the same rule `/checkout/{id}/promo` states, and this is always a
             * draft — `pay` has not run yet.
             *
             * It still STOPS before `pay` if the code is refused, and not after: on
             * an event, `pay` mints the hosted page and **holds a place for an
             * hour**. Refusing afterwards would take a place off sale for a purchase
             * we then abandon. `promoIgnored` now means *we tried twice and it is
             * not going to stick*, which is worth a sentence rather than a retry.
             */
            let order = draft;
            if (promoCode && order.promoCode !== promoCode) {
                order = await http.post(`/purchases/${order.id}/promo`, ApplyPromo.parse({ code: promoCode }), { schema: PurchaseCheckout });
                if (order.promoCode !== promoCode) {
                    return { purchase: order, promoIgnored: true };
                }
            }
            const paid = await http.post(`/purchases/${order.id}/pay`, urls(order.id), {
                schema: PurchaseCheckout,
                idempotencyKey: newIdempotencyKey(),
            });
            return { purchase: paid, promoIgnored: false };
        },
        onSuccess: ({ purchase }) => queryClient.setQueryData(purchaseKeys.one(purchase.id), purchase),
    });
}
/**
 * Ask for the hosted page on a purchase that already exists — the stranger's
 * second step, and the « Finaliser le paiement » on a draft.
 *
 * Calling it again replaces the page **and keeps the deadline**: the hour is
 * the hold's, not the page's, and it does not renew. On a stale draft this can
 * now answer `EVENT_HOLDS_BUSY` or `EVENT_AT_CAPACITY` where it would once
 * have succeeded, so the screen re-reads the purchase before drawing the
 * button and handles the refusal where it draws it.
 */
export function usePayPurchase(http, id) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (urls) => http.post(`/purchases/${id}/pay`, urls, {
            schema: PurchaseCheckout,
            idempotencyKey: newIdempotencyKey(),
        }),
        onSuccess: (purchase) => queryClient.setQueryData(purchaseKeys.one(purchase.id), purchase),
    });
}
/**
 * A refusal, as a key the screen can print.
 *
 * Two of these are **not errors** and must not be rendered as one:
 *
 *   `ALREADY_OWNED`     — they bought it already. Take them to it.
 *   `ALREADY_ENTITLED`  — their membership includes it. Take them to it.
 *
 * A practitioner clicking « Acheter » on a course their tier already covers is
 * being told good news; a red box saying « 409 » is that news told badly. The
 * caller branches on `purchaseIsGoodNews` before printing anything.
 *
 * And the one distinction worth the most care:
 *
 *   `EVENT_AT_CAPACITY` — the places are *taken*. « Complet », and final.
 *   `EVENT_HOLDS_BUSY`  — the remaining places are on somebody else's open
 *                         payment page. **Not « Complet »**: the payment may
 *                         not finish, so coming back in a few minutes is real
 *                         advice. Holds are capped below capacity precisely so
 *                         holds alone can never read « Complet ».
 */
export function purchaseRefusalKey(error) {
    if (!isApiError(error))
        return 'buy.failed';
    switch (error.code) {
        case 'ALREADY_OWNED':
            return 'buy.alreadyOwned';
        case 'ALREADY_ENTITLED':
            return 'buy.alreadyEntitled';
        case 'CONTENT_NOT_FOR_SALE':
            return 'buy.notForSale';
        case 'CONTENT_TYPE_NOT_SELLABLE':
            return 'buy.notSellable';
        case 'EVENT_ALREADY_STARTED':
            return 'buy.eventStarted';
        case 'EVENT_AT_CAPACITY':
            return 'buy.eventFull';
        case 'EVENT_HOLDS_BUSY':
            return 'buy.eventHoldsBusy';
        case 'PROMO_NOT_REDEEMABLE':
            return promoRefusalKey(error.details);
        case 'CHECKOUT_NOT_PAYABLE':
            return 'buy.notPayable';
        case 'CONTENT_NOT_FOUND':
        case 'EVENT_NOT_FOUND':
            return 'buy.notFound';
        /*
         * Two deployment faults, told apart from everything above by who can act:
         * nobody, on this side of the screen. `CHECKOUT_RETURN_URL_NOT_ALLOWED` is
         * our own `successUrl` origin missing from the server's allowlist, and
         * `STRIPE_NOT_CONFIGURED` is a server with no Stripe key — which is every
         * development stack, by design. « Try again » is the wrong advice for
         * both: trying again does exactly the same thing.
         */
        case 'CHECKOUT_RETURN_URL_NOT_ALLOWED':
        case 'STRIPE_NOT_CONFIGURED':
            return 'buy.misconfigured';
        default:
            return 'buy.failed';
    }
}
/**
 * Why a code was refused — `details.reason`, not the code on the envelope.
 *
 * `PROMO_NOT_REDEEMABLE` is one code for eight situations, and they are not
 * one sentence. The one the handoff calls out is **`ITEM_NOT_ELIGIBLE`**: the
 * code is real, live and redeemable, and simply not for this course — which a
 * member can act on (« try it on the membership ») in a way that « expired »
 * is not. Telling somebody their valid code has run out is a small lie that
 * costs a sale.
 *
 * Unknown reasons fall to the general sentence rather than printing a code at
 * somebody; the enum has grown once already.
 */
export function promoRefusalKey(details) {
    const reason = typeof details === 'object' && details !== null && 'reason' in details
        ? details.reason
        : undefined;
    switch (reason) {
        case 'ITEM_NOT_ELIGIBLE':
            return 'buy.promoNotForItem';
        case 'EXPIRED':
            return 'buy.promoExpired';
        case 'NOT_STARTED':
            return 'buy.promoNotStarted';
        case 'DISABLED':
            return 'buy.promoDisabled';
        case 'EXHAUSTED':
            return 'buy.promoExhausted';
        case 'CUSTOMER_LIMIT_REACHED':
            return 'buy.promoAlreadyUsed';
        case 'SEGMENT_NOT_ELIGIBLE':
        case 'PLAN_NOT_ELIGIBLE':
            return 'buy.promoNotForYou';
        default:
            return 'buy.promoNotRedeemable';
    }
}
/**
 * Whether this refusal means *you already have it* rather than *this did not
 * work* — the two codes that open the item instead of printing an error.
 */
export function purchaseIsGoodNews(error) {
    return isApiError(error) && (error.code === 'ALREADY_OWNED' || error.code === 'ALREADY_ENTITLED');
}
/** What this member owns, as a set of content item ids. */
export function ownedIds(purchases) {
    return new Set((purchases?.items ?? []).map((item) => item.contentItemId));
}
/** This member's purchase of one item, if they have one. */
export function purchaseOf(purchases, contentItemId) {
    return purchases?.items.find((item) => item.contentItemId === contentItemId) ?? null;
}
/**
 * Whether a locked row can be bought on its own.
 *
 * Null `priceCents` means *not sold separately* — printables and podcasts —
 * so the screen draws a plain lock there and an offer where there is a price.
 * One field, two states, and nothing branches on the content type.
 */
export function isSoldSeparately(item) {
    return typeof item.priceCents === 'number';
}
// ── The public door, for somebody with no account ──────────────────────────
/**
 * What the website's buy link lands on.
 *
 * `GET /site/courses/{slug}` and `GET /site/events/{slug}` are `@Public()`, so
 * a stranger can be shown the item before they have anything. They are the
 * only reads that turn the **slug** in the link into the **id** `POST
 * /purchases` takes — the member content reads answer an id and no slug, and
 * they are authenticated anyway.
 *
 * `id` arrived on 12 October (request 21) and is on the DETAIL reads only; the
 * site's own catalogue cards link by slug and post nothing. Declared required
 * here, unlike everything else this round, because this read is useless
 * without it: a page we cannot start a purchase from is a dead screen, and a
 * loud parse failure beats a button that does nothing.
 */
export const SiteCourse = z.object({
    id: z.string().uuid(),
    slug: z.string(),
    title: z.string(),
    summary: z.string().nullable(),
    coverUrl: z.string().nullable(),
    priceCents: z.number().int().nonnegative().nullable(),
    walkaway: z.array(z.string()).default([]),
    modules: z
        .array(z.object({ title: z.string(), durationSeconds: z.number().int().nullable() }))
        .default([]),
    durationSeconds: z.number().int().nullable(),
});
export const SiteEvent = z.object({
    id: z.string().uuid(),
    slug: z.string(),
    title: z.string(),
    summary: z.string().nullable(),
    coverUrl: z.string().nullable(),
    startsAt: z.string().datetime(),
    endsAt: z.string().datetime().nullable(),
    host: z.string().nullable(),
    priceCents: z.number().int().nonnegative().nullable(),
    soldOut: z.boolean(),
});
/**
 * One item by slug, for the buy screen. **Give it the PUBLIC client** — the
 * whole point is that it works with no session.
 */
export function useSiteCourse(http, slug, locale) {
    return useQuery({
        queryKey: ['site', 'courses', slug ?? '', locale],
        enabled: Boolean(slug),
        queryFn: () => http.get(`/site/courses/${slug}`, { schema: SiteCourse, locale }),
    });
}
export function useSiteEvent(http, slug, locale) {
    return useQuery({
        queryKey: ['site', 'events', slug ?? '', locale],
        enabled: Boolean(slug),
        queryFn: () => http.get(`/site/events/${slug}`, { schema: SiteEvent, locale }),
    });
}
/**
 * A stranger buys: the account and the purchase in one transaction, then the
 * payment page.
 *
 * Public — no token is sent, and the purchase id that comes back **is the
 * capability**. One mutation for both calls for the same reason as
 * `useBuyNow`: the next thing they should see is Stripe.
 *
 * No promo re-check here, unlike `useBuyNow`: there is no open draft to be
 * handed back, because the account did not exist a moment ago.
 */
export function useBuyAsGuest(http) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ input, urls, }) => {
            const draft = await http.post('/purchases', StartPurchase.parse(input), {
                schema: PurchaseCheckout,
                idempotencyKey: newIdempotencyKey(),
            });
            return http.post(`/purchases/${draft.id}/pay`, urls(draft.id), {
                schema: PurchaseCheckout,
                idempotencyKey: newIdempotencyKey(),
            });
        },
        onSuccess: (purchase) => queryClient.setQueryData(purchaseKeys.one(purchase.id), purchase),
    });
}
/**
 * Starting a purchase as a stranger, refused.
 *
 * `EMAIL_ALREADY_REGISTERED` is the one that is not an error: they have an
 * account and the fix is to sign in, not to pick another address. Everything
 * else falls through to the shared mapper, which both paths already share.
 */
export function guestRefusalKey(error) {
    if (isApiError(error) && error.code === 'EMAIL_ALREADY_REGISTERED')
        return 'buy.haveAccount';
    return purchaseRefusalKey(error);
}
/** Whether this refusal means *sign in instead*. */
export function guestHasAccount(error) {
    return isApiError(error) && error.code === 'EMAIL_ALREADY_REGISTERED';
}
/* ───────────────────────────────────────────────────────────────────────────
 * Quoting a promo code before anything is committed (ISS-02 / ISS-04).
 * ─────────────────────────────────────────────────────────────────────────── */
/**
 * The refusals the quote names, so a screen can say which one it is.
 *
 * `z.string()` and **not** `z.enum`, deliberately. The document lists eight
 * today; an enum here would fail the whole parse the day a ninth is added, and
 * a new member is the one API change that has already taken a screen down on
 * this platform (the back office's payments screen, 7 Oct). `promoQuoteKey`
 * has a default branch, so an unknown name costs a general sentence rather
 * than the price the member came to see.
 */
export const PROMO_QUOTE_REFUSALS = [
    'DISABLED',
    'NOT_STARTED',
    'EXPIRED',
    'EXHAUSTED',
    'CUSTOMER_LIMIT_REACHED',
    'SEGMENT_NOT_ELIGIBLE',
    'PLAN_NOT_ELIGIBLE',
    'ITEM_NOT_ELIGIBLE',
];
export const PromoQuote = z.object({
    code: z.string(),
    redeemable: z.boolean(),
    refusal: z.string().nullable(),
    subtotalCents: z.number().int(),
    discountCents: z.number().int(),
    discountedSubtotalCents: z.number().int(),
});
/**
 * What a code is worth here — **without committing it to anything**.
 *
 * The « Appliquer » button's whole job. Two screens were taking a typed code
 * straight to Stripe with no confirmation that it was even valid, which is
 * where ISS-02 and ISS-04 come from: the member learns on the payment page, or
 * from the receipt.
 *
 * `contentItemId` is how a single purchase is quoted and `planId` how a
 * membership is; the service resolves the item's own type from the id, so a
 * code whose `appliesTo` does not cover courses and events answers
 * `ITEM_NOT_ELIGIBLE` rather than quoting a discount the redemption would then
 * refuse. **That is every code written before single purchases existed**, and
 * deliberately so.
 *
 * **Open to a stranger when it is asked with `contentItemId`** (13 Oct 2026,
 * request 23 — asked and built the same day). `@OptionalAuth`, not public: no
 * token means nobody, a token means whoever it says, and a *bad* token is
 * still refused rather than read as anonymous — so a member whose session
 * expires on the buy screen gets a 401 they can act on instead of a stranger's
 * price with nothing saying the session had gone.
 *
 * Naming a `planId`, or naming neither, is **401 for a tokenless caller**: a
 * bare quote is a membership quote, and a membership is bought from inside an
 * account. Neither is sent from the buy screen.
 *
 * An anonymous caller is quoted in the **PARENT** segment — the same segment
 * `POST /purchases` creates a guest in, declared once on their side and pinned
 * by a test that reads the creation off disk. So **what this says is what the
 * till says**: a `PRACTITIONER`-targeted code answers `SEGMENT_NOT_ELIGIBLE`
 * here *and* refuses at creation, measured in both directions.
 *
 * One refusal is **unreachable from this route, for anybody**:
 * `CUSTOMER_LIMIT_REACHED` is counted where the code is consumed, not where it
 * is quoted, so a member over their limit is quoted `redeemable: true`. That
 * is the status quo for signed-in callers too and is enforced at the till; it
 * is noted here so the gap is known rather than discovered.
 */
export function quotePromo(http, input) {
    return http.get('/promo-codes/quote', {
        query: {
            code: input.code,
            subtotalCents: input.subtotalCents,
            ...(input.contentItemId ? { contentItemId: input.contentItemId } : {}),
            ...(input.planId ? { planId: input.planId } : {}),
        },
        schema: PromoQuote,
    });
}
export function useQuotePromo(http) {
    return useMutation({
        mutationFn: (input) => quotePromo(http, input),
    });
}
/**
 * A quote's refusal as a key — the same sentences the redemption refusals use,
 * because the member does not care which call found out.
 *
 * `null` for a redeemable code: there is nothing to say, the discount says it.
 */
export function promoQuoteKey(quote) {
    if (!quote || quote.redeemable)
        return null;
    switch (quote.refusal) {
        case 'ITEM_NOT_ELIGIBLE':
        case 'PLAN_NOT_ELIGIBLE':
            return 'buy.promoNotForItem';
        case 'EXPIRED':
            return 'buy.promoExpired';
        case 'NOT_STARTED':
            return 'buy.promoNotStarted';
        case 'DISABLED':
            return 'buy.promoDisabled';
        case 'EXHAUSTED':
            return 'buy.promoExhausted';
        case 'CUSTOMER_LIMIT_REACHED':
            return 'buy.promoAlreadyUsed';
        case 'SEGMENT_NOT_ELIGIBLE':
            return 'buy.promoNotForYou';
        default:
            return 'buy.promoNotRedeemable';
    }
}
/**
 * Why a QUOTE failed outright, as a key — a 404, not a refusal.
 *
 * The quote has two different ways of saying no and they are not one sentence:
 * a code that exists and cannot apply is a **200 carrying `refusal`**
 * (`promoQuoteKey`), and a string nobody has ever used is a **404
 * `PROMO_NOT_FOUND`**. That split is the back office's duplicate-code detector
 * and it holds for an anonymous caller too, so the buy screen can tell « this
 * code has expired » from « there is no such code », which is the difference
 * between a wasted code and a typo.
 *
 * A 401 is deliberately NOT given a buyer-facing sentence here. From this
 * screen it means one of two things — the session went while they were reading,
 * or a plan was named on an anonymous quote — and neither is something the
 * sentence under a promo field can fix.
 */
export function promoQuoteErrorKey(error) {
    if (isApiError(error) && error.code === 'PROMO_NOT_FOUND')
        return 'buy.promoUnknown';
    return 'buy.promoNotRedeemable';
}
//# sourceMappingURL=purchases.js.map